import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatGBP } from "@/lib/api";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login", { state: { from: "/account" } });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) api.get("/orders/me").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  if (loading || !user) return <div className="py-32 text-center text-[#5C4A4D]">Loading...</div>;

  return (
    <div className="bg-[#FFF4E0] min-h-screen" data-testid="account-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-16">
        <div className="overline mb-3">Members</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-[#1A0F11]">My Account</h1>
        <p className="text-[#5C4A4D] mt-3">Welcome back, {user.name.split(" ")[0]}.</p>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mt-12">
          <aside className="md:col-span-3 space-y-2">
            <div className="bg-[#FDF1EC] p-6">
              <div className="overline text-xs">Profile</div>
              <div className="font-serif-display text-xl text-[#1A0F11] mt-2">{user.name}</div>
              <div className="text-sm text-[#5C4A4D] mt-1">{user.email}</div>
              {user.role === "admin" && (
                <Link to="/admin" className="btn-outline w-full mt-4" data-testid="account-admin-link">Admin dashboard</Link>
              )}
              <button onClick={logout} className="mt-4 text-xs uppercase tracking-[0.22em] text-[#831132] hover:underline" data-testid="account-logout">
                Sign out
              </button>
            </div>
          </aside>
          <div className="md:col-span-9">
            <h2 className="font-serif-display text-3xl text-[#1A0F11] mb-6">Orders</h2>
            {orders.length === 0 ? (
              <div className="bg-[#FDF1EC] p-12 text-center">
                <p className="font-serif-display text-2xl text-[#1A0F11]">No orders yet.</p>
                <Link to="/shop" className="btn-primary mt-6 inline-flex">Find your crown</Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.order_id} className="bg-[#FDF1EC] p-6" data-testid={`order-row-${o.order_id}`}>
                    <div className="flex justify-between items-start flex-wrap gap-4">
                      <div>
                        <div className="overline">Order {o.order_id.replace("order_", "")}</div>
                        <div className="font-serif-display text-xl text-[#1A0F11] mt-1">{o.items.length} item{o.items.length > 1 ? "s" : ""}</div>
                        <div className="text-xs text-[#5C4A4D] mt-1">{new Date(o.created_at).toLocaleDateString("en-GB")}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xs uppercase tracking-[0.18em] ${o.status === "confirmed" ? "text-[#2D4A22]" : "text-[#831132]"}`}>{o.status}</div>
                        <div className="font-serif-display text-2xl text-[#1A0F11] mt-1">{formatGBP(o.total)}</div>
                      </div>
                    </div>
                    <div className="border-t border-[#E8D8C8] mt-4 pt-4 flex gap-4 overflow-x-auto">
                      {o.items.map((it, i) => (
                        <img key={i} src={it.image_url} alt={it.name} className="w-16 h-20 object-cover flex-none" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
